import { z } from 'zod';
import {
  CostCodeCategorySchema,
  CostCodeSchema,
  costCodeKey,
  type CostCode,
  type CostCodeCategory,
  type CostType,
  MAX_MARKUP_MILLIONTHS,
} from '../../domain/entities/cost-code.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Managing a company's chart of accounts.
 *
 * The same shape as `tags.ts`, for the same reasons, with two differences
 * that matter:
 *
 * 1. **A duplicate is refused by number, not by name.** Two codes called
 *    "Framing" are a judgement call a company may want; two codes numbered
 *    `3010` make every report ambiguous.
 * 2. **Nothing is ever deleted.** A code a bill from two years ago points
 *    at cannot go away without turning that bill into an orphan, so
 *    `retireCostCode` sets `active: false` and there is no delete. Tags can
 *    be deleted because `tagNames` skips an id with no tag behind it; a
 *    missing cost code is a hole in a financial report.
 *
 * Uniqueness is checked here rather than in a rule because a rule can see
 * the document being written and cannot ask whether another document
 * already holds that number. The check is therefore advisory under
 * concurrency — two people creating `3010` at the same moment both pass —
 * and the honest fix is the counter-and-transaction pattern change orders
 * use. That is overkill for a settings screen two people touch a year, and
 * P5-01b records the trade rather than pretending it does not exist.
 */
export class CostCodeError extends Error {
  readonly reason: 'invalid' | 'duplicate' | 'unknownCategory';

  constructor(reason: 'invalid' | 'duplicate' | 'unknownCategory', message: string) {
    super(message);
    this.name = 'CostCodeError';
    this.reason = reason;
  }
}

export const SaveCategoryInputSchema = z.object({
  name: z.string().trim().min(1, 'A category needs a name').max(80),
  rangeStart: z.number().int().min(0).max(999_999),
  rangeEnd: z.number().int().min(0).max(999_999),
  order: z.number().int().min(0).optional(),
  active: z.boolean().optional(),
});
export type SaveCategoryInput = z.input<typeof SaveCategoryInputSchema>;

export const SaveCostCodeInputSchema = z.object({
  number: z.string().trim().min(1, 'A cost code needs a number').max(20),
  name: z.string().trim().min(1, 'A cost code needs a name').max(120),
  categoryId: z.string().min(1, 'A cost code needs a category'),
  costTypes: z.array(z.custom<CostType>()).optional(),
  defaultMarkupMillionths: z.number().int().min(0).max(MAX_MARKUP_MILLIONTHS).optional(),
  active: z.boolean().optional(),
  externalId: z.string().trim().max(100).optional(),
});
export type SaveCostCodeInput = z.input<typeof SaveCostCodeInputSchema>;

export interface CostCodeContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
}

type ReadContext = Pick<CostCodeContext, 'data' | 'companyId'>;

function categoryRepo(context: ReadContext) {
  return context.data.collection<CostCodeCategory>(
    { companyId: context.companyId },
    'costCodeCategories',
  );
}

function codeRepo(context: ReadContext) {
  return context.data.collection<CostCode>({ companyId: context.companyId }, 'costCodes');
}

/**
 * Categories in display order.
 *
 * Ordered by `order` in the query rather than sorted after, so a long chart
 * pages correctly. `groupCostCodes` sorts again as a tiebreak, which costs
 * nothing and means a caller holding an unordered list still renders right.
 */
export async function listCostCodeCategories(context: ReadContext): Promise<CostCodeCategory[]> {
  const page = await categoryRepo(context).list({
    orderBy: [{ field: 'order', direction: 'asc' }],
  });
  return [...page.items];
}

export async function listCostCodes(context: ReadContext): Promise<CostCode[]> {
  const page = await codeRepo(context).list({
    orderBy: [{ field: 'number', direction: 'asc' }],
  });
  return [...page.items];
}

export function subscribeToCostCodeCategories(
  context: ReadContext,
  onChange: (categories: readonly CostCodeCategory[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return categoryRepo(context).subscribe(
    { orderBy: [{ field: 'order', direction: 'asc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

export function subscribeToCostCodes(
  context: ReadContext,
  onChange: (codes: readonly CostCode[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return codeRepo(context).subscribe(
    { orderBy: [{ field: 'number', direction: 'asc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

export async function createCostCodeCategory(
  context: CostCodeContext,
  input: SaveCategoryInput,
): Promise<CostCodeCategory> {
  const parsed = SaveCategoryInputSchema.safeParse(input);
  if (!parsed.success) throw new CostCodeError('invalid', parsed.error.issues[0]!.message);

  const existing = await listCostCodeCategories(context);
  const now = context.clock.now().toISOString();
  const category = {
    id: newId(),
    companyId: context.companyId,
    name: parsed.data.name,
    rangeStart: parsed.data.rangeStart,
    rangeEnd: parsed.data.rangeEnd,
    // Appended, not inserted. A new category goes at the end of the list
    // and a person drags it where they want it, which is one decision each
    // rather than a guess at where it belongs.
    order: parsed.data.order ?? existing.length,
    active: parsed.data.active ?? true,
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  const checked = CostCodeCategorySchema.safeParse(category);
  if (!checked.success) throw new CostCodeError('invalid', checked.error.issues[0]!.message);

  await categoryRepo(context).create(checked.data);
  return checked.data;
}

export async function saveCostCodeCategory(
  context: CostCodeContext,
  id: string,
  input: SaveCategoryInput,
): Promise<CostCodeCategory> {
  const parsed = SaveCategoryInputSchema.safeParse(input);
  if (!parsed.success) throw new CostCodeError('invalid', parsed.error.issues[0]!.message);

  const existing = await categoryRepo(context).get(id);
  if (!existing) throw new NotFoundError('costCodeCategories', id);

  const patch = {
    name: parsed.data.name,
    rangeStart: parsed.data.rangeStart,
    rangeEnd: parsed.data.rangeEnd,
    order: parsed.data.order ?? existing.order,
    active: parsed.data.active ?? existing.active,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  // Validated as a whole rather than field by field, so the range
  // refinement runs: an edit that moves `rangeStart` past the existing
  // `rangeEnd` is as invalid as a create that does.
  const checked = CostCodeCategorySchema.safeParse({ ...existing, ...patch });
  if (!checked.success) throw new CostCodeError('invalid', checked.error.issues[0]!.message);

  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId },
      collection: 'costCodeCategories',
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return checked.data;
}

export async function createCostCode(
  context: CostCodeContext,
  input: SaveCostCodeInput,
): Promise<CostCode> {
  const parsed = SaveCostCodeInputSchema.safeParse(input);
  if (!parsed.success) throw new CostCodeError('invalid', parsed.error.issues[0]!.message);

  await assertCategoryExists(context, parsed.data.categoryId);
  const existing = await listCostCodes(context);
  const clash = existing.find(
    (code) => costCodeKey(code.number) === costCodeKey(parsed.data.number),
  );
  if (clash) {
    throw new CostCodeError(
      'duplicate',
      `Code ${clash.number} is already “${clash.name}”. Two codes with one number make every report ambiguous.`,
    );
  }

  const now = context.clock.now().toISOString();
  const code = {
    id: newId(),
    companyId: context.companyId,
    number: parsed.data.number,
    name: parsed.data.name,
    categoryId: parsed.data.categoryId,
    costTypes: parsed.data.costTypes ?? [],
    defaultMarkupMillionths: parsed.data.defaultMarkupMillionths ?? 0,
    active: parsed.data.active ?? true,
    ...(parsed.data.externalId ? { externalId: parsed.data.externalId } : {}),
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  const checked = CostCodeSchema.safeParse(code);
  if (!checked.success) throw new CostCodeError('invalid', checked.error.issues[0]!.message);

  await codeRepo(context).create(checked.data);
  return checked.data;
}

/**
 * Edit a code, renumbering included.
 *
 * **The id never changes**, which is the whole point: every estimate line,
 * purchase order, bill and invoice holds `costCodeId`, so renumbering is a
 * rename and a bill from two years ago keeps reporting under the code it
 * was filed against. Renumbering also does not refile — if somebody wants
 * the code in another category they change `categoryId`, which is a
 * separate act with a separate undo.
 */
export async function saveCostCode(
  context: CostCodeContext,
  id: string,
  input: SaveCostCodeInput,
): Promise<CostCode> {
  const parsed = SaveCostCodeInputSchema.safeParse(input);
  if (!parsed.success) throw new CostCodeError('invalid', parsed.error.issues[0]!.message);

  const existing = await codeRepo(context).get(id);
  if (!existing) throw new NotFoundError('costCodes', id);

  await assertCategoryExists(context, parsed.data.categoryId);

  const others = (await listCostCodes(context)).filter((code) => code.id !== id);
  const clash = others.find((code) => costCodeKey(code.number) === costCodeKey(parsed.data.number));
  if (clash) {
    throw new CostCodeError(
      'duplicate',
      `Code ${clash.number} is already “${clash.name}”. Two codes with one number make every report ambiguous.`,
    );
  }

  const patch = {
    number: parsed.data.number,
    name: parsed.data.name,
    categoryId: parsed.data.categoryId,
    costTypes: parsed.data.costTypes ?? existing.costTypes,
    defaultMarkupMillionths:
      parsed.data.defaultMarkupMillionths ?? existing.defaultMarkupMillionths,
    active: parsed.data.active ?? existing.active,
    ...(parsed.data.externalId === undefined ? {} : { externalId: parsed.data.externalId }),
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  const checked = CostCodeSchema.safeParse({ ...existing, ...patch });
  if (!checked.success) throw new CostCodeError('invalid', checked.error.issues[0]!.message);

  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId },
      collection: 'costCodes',
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return checked.data;
}

/**
 * Stop offering a code without removing it.
 *
 * There is no `deleteCostCode`, and the absence is deliberate. A code is
 * pointed at by every financial record ever filed against it; removing one
 * would turn a two-year-old bill into a line reporting under nothing. So
 * the only way out is `active: false`, which keeps every historical report
 * correct and keeps the code off the pickers.
 */
export async function retireCostCode(context: CostCodeContext, id: string): Promise<CostCode> {
  const existing = await codeRepo(context).get(id);
  if (!existing) throw new NotFoundError('costCodes', id);
  if (!existing.active) return existing;
  return saveCostCode(context, id, {
    number: existing.number,
    name: existing.name,
    categoryId: existing.categoryId,
    costTypes: existing.costTypes,
    defaultMarkupMillionths: existing.defaultMarkupMillionths,
    active: false,
  });
}

export async function reinstateCostCode(context: CostCodeContext, id: string): Promise<CostCode> {
  const existing = await codeRepo(context).get(id);
  if (!existing) throw new NotFoundError('costCodes', id);
  if (existing.active) return existing;
  return saveCostCode(context, id, {
    number: existing.number,
    name: existing.name,
    categoryId: existing.categoryId,
    costTypes: existing.costTypes,
    defaultMarkupMillionths: existing.defaultMarkupMillionths,
    active: true,
  });
}

/**
 * Reorder the categories.
 *
 * The same shape as `reorderSchedulePhases`: the whole order is written in
 * one commit, because writing one row's new position leaves the others
 * holding numbers that no longer agree with it.
 */
export async function reorderCostCodeCategories(
  context: CostCodeContext,
  orderedIds: readonly string[],
): Promise<void> {
  const existing = await listCostCodeCategories(context);
  const byId = new Map(existing.map((category) => [category.id, category] as const));
  const now = context.clock.now().toISOString();

  const writes = orderedIds.flatMap((id, index) => {
    const category = byId.get(id);
    if (!category || category.order === index) return [];
    return [
      {
        kind: 'update' as const,
        scope: { companyId: context.companyId },
        collection: 'costCodeCategories' as const,
        id,
        patch: { order: index, updatedAt: now, updatedBy: context.uid },
        expect: expectUnchanged(category),
      },
    ];
  });

  if (writes.length > 0) await context.data.commit(writes);
}

async function assertCategoryExists(context: CostCodeContext, categoryId: string): Promise<void> {
  const category = await categoryRepo(context).get(categoryId);
  if (!category) {
    // Checked before writing rather than left to a dangling reference,
    // because a code in no category is a code that appears in `orphans` on
    // the settings page and nowhere on an estimate.
    throw new CostCodeError('unknownCategory', 'That category no longer exists');
  }
}

/**
 * Create the categories and codes a parsed CSV describes.
 *
 * `parseCostCodeCsv` decides what the file says; this decides what to do
 * about it, and the awkward part is matching a category **by name** against
 * what already exists. A company importing twice should not end up with two
 * "Rough structure" categories, so an existing one with the same name
 * (spacing and case set aside) is reused rather than duplicated.
 *
 * Written one document at a time rather than in a batch, deliberately. A
 * four-hundred-code import exceeds `MAX_BATCH_WRITES`, and splitting it
 * into batches would make a partial failure land on a batch boundary
 * nobody can see. One at a time means a partial import is **exactly** the
 * rows that succeeded, and the result says which — a state a person can
 * look at and finish.
 */
export interface ImportResult {
  readonly categoriesCreated: number;
  readonly codesCreated: number;
  /** Rows the write refused, on top of the ones the parser refused. */
  readonly failed: readonly { readonly number: string; readonly reason: string }[];
}

export async function importCostCodes(
  context: CostCodeContext,
  parsed: {
    readonly rows: readonly {
      readonly number: string;
      readonly name: string;
      readonly categoryName: string;
      readonly costTypes: readonly CostType[];
      readonly markupMillionths: number;
    }[];
    readonly categoryNames: readonly string[];
  },
  rangeFor: (categoryName: string) => { start: number; end: number },
): Promise<ImportResult> {
  const existing = await listCostCodeCategories(context);
  const byName = new Map(
    existing.map((category) => [categoryKey(category.name), category] as const),
  );
  const failed: { number: string; reason: string }[] = [];
  let categoriesCreated = 0;

  for (const name of parsed.categoryNames) {
    if (byName.has(categoryKey(name))) continue;
    const range = rangeFor(name);
    try {
      const created = await createCostCodeCategory(context, {
        name,
        rangeStart: range.start,
        rangeEnd: range.end,
      });
      byName.set(categoryKey(name), created);
      categoriesCreated += 1;
    } catch (cause) {
      failed.push({
        number: name,
        reason: cause instanceof Error ? cause.message : 'Could not create that category',
      });
    }
  }

  let codesCreated = 0;
  for (const row of parsed.rows) {
    const category = byName.get(categoryKey(row.categoryName));
    if (!category) {
      // Its category failed above. Reported against the code as well, so a
      // person reading the result does not have to join two lists.
      failed.push({ number: row.number, reason: `No category “${row.categoryName}”` });
      continue;
    }
    try {
      await createCostCode(context, {
        number: row.number,
        name: row.name,
        categoryId: category.id,
        costTypes: [...row.costTypes],
        defaultMarkupMillionths: row.markupMillionths,
      });
      codesCreated += 1;
    } catch (cause) {
      // Most often a number the company already has, which is the expected
      // outcome of importing a file twice. Reported, not retried.
      failed.push({
        number: row.number,
        reason: cause instanceof Error ? cause.message : 'Could not create that code',
      });
    }
  }

  return { categoriesCreated, codesCreated, failed };
}

/**
 * Two category names are the same name once case and spacing are set aside.
 *
 * The same reasoning as `costCodeKey` and `tagKey`: importing twice must
 * not leave a company with "Rough structure" and "rough  structure" as two
 * different categories.
 */
export function categoryKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}
